import {
  AlertTriangle,
  Check,
  Copy,
  Edit3,
  KeyRound,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Shield,
  Trash2,
  Users,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { API_CONFIG, fetchApp } from "../../services/apiService";

interface Role {
  id: string;
  name: string;
}

interface RoleForm {
  name: string;
}

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function formatNumber(value?: number | null) {
  return new Intl.NumberFormat("en-KE").format(Number(value || 0));
}

function normalizeErrorMessage(error: any, fallback: string) {
  const message = error?.detail || error?.message || fallback;

  if (typeof message === "string") return message;

  if (Array.isArray(message)) {
    return message
      .map((item) => item?.msg || item?.message || JSON.stringify(item))
      .join(", ");
  }

  try {
    return JSON.stringify(message);
  } catch {
    return fallback;
  }
}

function getInitials(name?: string) {
  if (!name) return "R";

  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function getRoleTone(roleName?: string) {
  const normalized = String(roleName || "").toLowerCase();

  if (normalized.includes("super")) {
    return {
      badge: "border-purple-100 bg-purple-50 text-purple-700",
      avatar: "bg-purple-600",
    };
  }

  if (normalized.includes("admin")) {
    return {
      badge: "border-blue-100 bg-blue-50 text-blue-700",
      avatar: "bg-blue-600",
    };
  }

  if (normalized.includes("manager")) {
    return {
      badge: "border-emerald-100 bg-emerald-50 text-emerald-700",
      avatar: "bg-emerald-600",
    };
  }

  if (normalized.includes("technician")) {
    return {
      badge: "border-orange-100 bg-orange-50 text-orange-700",
      avatar: "bg-orange-500",
    };
  }

  return {
    badge: "border-slate-200 bg-slate-50 text-slate-700",
    avatar: "bg-slate-950",
  };
}

function isPrivilegedRole(roleName?: string) {
  const normalized = String(roleName || "").toLowerCase();

  return (
    normalized.includes("super") ||
    normalized.includes("admin") ||
    normalized.includes("manager")
  );
}

function StatCard({
  title,
  value,
  helper,
  icon,
  tone,
}: {
  title: string;
  value: number;
  helper: string;
  icon: React.ReactNode;
  tone: string;
}) {
  return (
    <div className="group relative overflow-hidden rounded-3xl border border-white/80 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg">
      <div
        className={cn(
          "absolute right-[-34px] top-[-34px] h-24 w-24 rounded-full opacity-15 blur-2xl",
          tone,
        )}
      />

      <div className="relative flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-500">{title}</p>

          <h3 className="mt-1 text-3xl font-black tracking-tight text-slate-950">
            {formatNumber(value)}
          </h3>

          <p className="mt-1 text-xs font-medium text-slate-400">{helper}</p>
        </div>

        <div
          className={cn(
            "rounded-2xl p-3 text-white shadow-lg transition group-hover:scale-105",
            tone,
          )}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function MiniMetric({
  title,
  value,
  icon,
}: {
  title: string;
  value: number;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-white/15 p-2 text-white">{icon}</div>

        <div>
          <p className="text-xs font-medium text-white/60">{title}</p>
          <p className="text-xl font-black text-white">{formatNumber(value)}</p>
        </div>
      </div>
    </div>
  );
}

function RoleAvatar({ name }: { name: string }) {
  const tone = getRoleTone(name);

  return (
    <div
      className={cn(
        "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-sm font-black text-white shadow-sm",
        tone.avatar,
      )}
    >
      {getInitials(name)}
    </div>
  );
}

function RoleBadge({ name }: { name: string }) {
  const tone = getRoleTone(name);

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-black",
        tone.badge,
      )}
    >
      <Shield size={13} />
      {isPrivilegedRole(name) ? "Privileged" : "Standard"}
    </span>
  );
}

function EmptyState({
  searchQuery,
  onCreate,
}: {
  searchQuery: string;
  onCreate: () => void;
}) {
  return (
    <div className="rounded-3xl border border-dashed border-slate-200 bg-white px-4 py-16 text-center shadow-sm">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-100 text-slate-400">
        <Shield size={30} />
      </div>

      <h3 className="mt-4 text-lg font-black text-slate-950">
        {searchQuery ? "No roles found" : "No roles yet"}
      </h3>

      <p className="mx-auto mt-1 max-w-md text-sm font-medium leading-6 text-slate-500">
        {searchQuery
          ? "Try searching using a different role name."
          : "Create your first role so users can be assigned permissions and access levels."}
      </p>

      {!searchQuery && (
        <button
          onClick={onCreate}
          className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-slate-800"
        >
          <Plus size={17} />
          Add first role
        </button>
      )}
    </div>
  );
}

function RoleModal({
  open,
  mode,
  form,
  error,
  submitting,
  onClose,
  onSubmit,
  onChange,
}: {
  open: boolean;
  mode: "create" | "edit";
  form: RoleForm;
  error: string | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (event: React.FormEvent) => void;
  onChange: (next: RoleForm) => void;
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/50 px-4 py-6 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-xl overflow-hidden rounded-[2rem] bg-white shadow-2xl">
        <div className="relative overflow-hidden bg-slate-950 px-5 py-5 text-white">
          <div className="absolute right-[-70px] top-[-70px] h-40 w-40 rounded-full bg-blue-500/30 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-white p-3 text-slate-950 shadow-lg">
                <Shield size={22} />
              </div>

              <div>
                <h2 className="text-xl font-black">
                  {mode === "create" ? "Add Role" : "Edit Role"}
                </h2>

                <p className="mt-1 text-sm leading-5 text-white/60">
                  {mode === "create"
                    ? "Create a new system role for user access control."
                    : "Update this role name."}
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              type="button"
              disabled={submitting}
              className="rounded-2xl bg-white/10 p-2 text-white/70 transition hover:bg-white/15 hover:text-white disabled:opacity-50"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {error && (
          <div className="px-5 pt-5">
            <div className="flex items-start gap-2 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {error}
            </div>
          </div>
        )}

        <form
          onSubmit={onSubmit}
          className="max-h-[calc(92vh-112px)] overflow-y-auto px-5 py-5"
        >
          <div>
            <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
              Role Name <span className="text-rose-500">*</span>
            </label>

            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={form.name}
                onChange={(event) => onChange({ name: event.target.value })}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 pl-10 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100"
                placeholder="Example: Client Admin"
                autoFocus
              />
            </div>

            <p className="mt-1.5 text-xs font-medium text-slate-400">
              This name will appear in user assignment dropdowns.
            </p>
          </div>

          <div className="mt-5 flex flex-col-reverse gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  {mode === "create" ? "Create Role" : "Save Changes"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteDialog({
  open,
  role,
  loading,
  onClose,
  onConfirm,
}: {
  open: boolean;
  role: Role | null;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  if (!open || !role) return null;

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center bg-slate-950/50 px-4 py-6 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-[2rem] bg-white p-5 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-rose-50 p-3 text-rose-600">
            <Trash2 size={22} />
          </div>

          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-black text-slate-950">Delete Role</h2>

            <p className="mt-1 text-sm leading-6 text-slate-500">
              Are you sure you want to delete{" "}
              <span className="font-black text-slate-900">"{role.name}"</span>?
              This action cannot be undone.
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-rose-600 px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                Delete Role
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

const RolesPage: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageError, setPageError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState<string>("");

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [form, setForm] = useState<RoleForm>({ name: "" });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [copiedId, setCopiedId] = useState<string | null>(null);

  const loadRoles = async () => {
    setLoading(true);
    setPageError(null);

    try {
      const data = await fetchApp<Role[] | { items?: Role[]; data?: Role[] }>(
        API_CONFIG.endpoints.roles,
      );

      const safeRoles = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.data)
            ? data.data
            : [];

      setRoles(safeRoles);
    } catch (err: any) {
      console.error(err);
      setPageError(normalizeErrorMessage(err, "Failed to fetch roles."));
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  const filteredRoles = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) return roles;

    return roles.filter((role) =>
      String(role.name || "")
        .toLowerCase()
        .includes(query),
    );
  }, [roles, searchQuery]);

  const privilegedRoles = useMemo(
    () => roles.filter((role) => isPrivilegedRole(role.name)).length,
    [roles],
  );

  const standardRoles = Math.max(roles.length - privilegedRoles, 0);

  const openCreateModal = () => {
    setModalMode("create");
    setSelectedRole(null);
    setForm({ name: "" });
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (role: Role) => {
    setModalMode("edit");
    setSelectedRole(role);
    setForm({ name: role.name || "" });
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    if (submitting) return;

    setShowModal(false);
    setSelectedRole(null);
    setForm({ name: "" });
    setFormError(null);
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      setFormError("Role name is required.");
      return false;
    }

    const duplicate = roles.some((role) => {
      const sameName =
        role.name.trim().toLowerCase() === form.name.trim().toLowerCase();

      if (modalMode === "edit" && selectedRole) {
        return sameName && role.id !== selectedRole.id;
      }

      return sameName;
    });

    if (duplicate) {
      setFormError("A role with this name already exists.");
      return false;
    }

    setFormError(null);
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);
    setFormError(null);
    setPageError(null);

    try {
      if (modalMode === "create") {
        const createdRole = await fetchApp<Role>(API_CONFIG.endpoints.roles, {
          method: "POST",
          body: JSON.stringify({ name: form.name.trim() }),
        });

        setRoles((prev) => [...prev, createdRole]);
      } else if (selectedRole) {
        const updatedRole = await fetchApp<Role>(
          `${API_CONFIG.endpoints.roles}/${selectedRole.id}`,
          {
            method: "PUT",
            body: JSON.stringify({ name: form.name.trim() }),
          },
        );

        setRoles((prev) =>
          prev.map((role) =>
            role.id === selectedRole.id ? updatedRole : role,
          ),
        );
      }

      closeModal();
    } catch (err: any) {
      console.error(err);
      setFormError(
        normalizeErrorMessage(
          err,
          modalMode === "create"
            ? "Error adding role."
            : "Error updating role.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleteLoading(true);
    setPageError(null);

    try {
      await fetchApp<void>(`${API_CONFIG.endpoints.roles}/${deleteTarget.id}`, {
        method: "DELETE",
      });

      setRoles((prev) => prev.filter((role) => role.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err: any) {
      console.error(err);
      setPageError(normalizeErrorMessage(err, "Error deleting role."));
    } finally {
      setDeleteLoading(false);
    }
  };

  const copyRoleId = async (roleId: string) => {
    try {
      await navigator.clipboard.writeText(roleId);
      setCopiedId(roleId);

      setTimeout(() => setCopiedId(null), 1500);
    } catch (err) {
      console.error("Failed to copy role ID:", err);
    }
  };

  return (
    <div className="min-h-full w-full bg-[linear-gradient(180deg,#eef5ff_0%,#f8fbff_100%)] px-2 py-3 sm:px-3 md:px-4 md:py-4">
      <div className="w-full max-w-none space-y-4">
        <section className="relative overflow-hidden rounded-3xl bg-slate-950 p-5 text-white shadow-xl md:p-6">
          <div className="absolute right-[-80px] top-[-80px] h-64 w-64 rounded-full bg-blue-500/30 blur-3xl" />
          <div className="absolute bottom-[-100px] left-1/3 h-64 w-64 rounded-full bg-violet-500/20 blur-3xl" />

          <div className="relative flex flex-col justify-between gap-5 xl:flex-row xl:items-center">
            <div className="max-w-3xl">
              <div className="flex items-start gap-4">
                <div className="hidden rounded-3xl bg-white p-4 text-slate-950 shadow-xl sm:block">
                  <Shield size={28} />
                </div>

                <div>
                  <h1 className="text-3xl font-black tracking-tight md:text-4xl">
                    Role Management
                  </h1>

                  <p className="mt-2 max-w-2xl text-sm leading-6 text-white/65">
                    Create, update, and remove roles used to control system
                    access across clients, users, admins, and technicians.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[480px]">
              <MiniMetric
                title="Total Roles"
                value={roles.length}
                icon={<Shield size={18} />}
              />

              <MiniMetric
                title="Privileged"
                value={privilegedRoles}
                icon={<KeyRound size={18} />}
              />

              <MiniMetric
                title="Filtered"
                value={filteredRoles.length}
                icon={<Search size={18} />}
              />
            </div>
          </div>

          <div className="relative mt-6 flex flex-col gap-3 border-t border-white/10 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm font-medium text-white/60">
              Keep role names clear because they appear when assigning users to
              clients.
            </p>

            <button
              onClick={openCreateModal}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-blue-700"
            >
              <Plus size={17} />
              Add Role
            </button>
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            title="Total Roles"
            value={roles.length}
            helper="All system roles"
            icon={<Shield size={23} />}
            tone="bg-blue-600"
          />

          <StatCard
            title="Privileged"
            value={privilegedRoles}
            helper="Admin, super, manager roles"
            icon={<KeyRound size={23} />}
            tone="bg-purple-600"
          />

          <StatCard
            title="Standard"
            value={standardRoles}
            helper="Normal access roles"
            icon={<Users size={23} />}
            tone="bg-emerald-600"
          />

          <StatCard
            title="Filtered"
            value={filteredRoles.length}
            helper="Current search results"
            icon={<Search size={23} />}
            tone="bg-amber-500"
          />
        </section>

        <section className="rounded-3xl border border-white bg-white/95 p-4 shadow-sm backdrop-blur">
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-slate-950 p-3 text-white">
                <Search size={18} />
              </div>

              <div>
                <h2 className="font-black text-slate-950">Search & Actions</h2>

                <p className="mt-1 text-xs font-medium text-slate-400">
                  Search roles by name or create a new role.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex min-w-0 items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 transition focus-within:border-blue-400 focus-within:bg-white focus-within:ring-4 focus-within:ring-blue-100 sm:w-[420px]">
                <Search size={17} className="shrink-0 text-slate-400" />

                <input
                  type="text"
                  placeholder="Search roles..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="w-full border-none bg-transparent text-sm font-semibold text-slate-800 outline-none placeholder:text-slate-400"
                />

                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="rounded-full p-1 text-slate-400 transition hover:bg-slate-200 hover:text-slate-700"
                    type="button"
                  >
                    <X size={15} />
                  </button>
                )}
              </div>

              <button
                onClick={loadRoles}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <RefreshCw
                  size={17}
                  className={loading ? "animate-spin" : ""}
                />
                Refresh
              </button>

              <button
                onClick={openCreateModal}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white shadow-sm transition hover:bg-blue-700"
              >
                <Plus size={17} />
                Add Role
              </button>
            </div>
          </div>

          {pageError && (
            <div className="mt-4 flex items-start gap-2 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
              <AlertTriangle size={17} className="mt-0.5 shrink-0" />
              {pageError}
            </div>
          )}
        </section>

        {loading ? (
          <div className="flex items-center justify-center gap-3 rounded-3xl border border-white bg-white/95 px-4 py-16 text-sm font-black text-slate-500 shadow-sm">
            <Loader2 size={22} className="animate-spin text-blue-600" />
            Loading roles...
          </div>
        ) : filteredRoles.length === 0 ? (
          <EmptyState searchQuery={searchQuery} onCreate={openCreateModal} />
        ) : (
          <section className="overflow-hidden rounded-3xl border border-white bg-white/95 shadow-sm backdrop-blur">
            <div className="flex flex-col justify-between gap-3 border-b border-slate-100 p-4 md:flex-row md:items-center md:p-5">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-blue-50 p-3 text-blue-700">
                  <Shield size={20} />
                </div>

                <div>
                  <h2 className="font-black text-slate-950">Roles List</h2>

                  <p className="text-xs font-medium text-slate-400">
                    Showing {formatNumber(filteredRoles.length)} of{" "}
                    {formatNumber(roles.length)} roles
                  </p>
                </div>
              </div>

              {searchQuery && (
                <div className="rounded-2xl bg-slate-50 px-3 py-2 text-xs font-bold text-slate-500">
                  Search:{" "}
                  <span className="font-black text-slate-800">
                    “{searchQuery}”
                  </span>
                </div>
              )}
            </div>

            <div className="hidden overflow-x-auto lg:block">
              <table className="w-full min-w-[900px] border-collapse">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80 text-left text-[11px] font-black uppercase tracking-wide text-slate-400">
                    <th className="px-5 py-4">Role</th>
                    <th className="px-5 py-4">Access Level</th>
                    <th className="px-5 py-4">Role ID</th>
                    <th className="px-5 py-4 text-right">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredRoles.map((role) => (
                    <tr
                      key={role.id}
                      className="border-b border-slate-100 transition hover:bg-slate-50/80"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <RoleAvatar name={role.name} />

                          <div className="min-w-0">
                            <p className="max-w-[300px] truncate text-sm font-black text-slate-950">
                              {role.name}
                            </p>

                            <p className="mt-1 text-xs font-medium text-slate-400">
                              System access role
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <RoleBadge name={role.name} />
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex max-w-[360px] items-center gap-2 rounded-2xl bg-slate-50 px-3 py-2">
                          <p className="min-w-0 flex-1 truncate font-mono text-xs font-semibold text-slate-500">
                            {role.id}
                          </p>

                          <button
                            onClick={() => copyRoleId(role.id)}
                            className="rounded-xl p-1 text-slate-400 transition hover:bg-white hover:text-slate-700"
                            title="Copy role ID"
                          >
                            {copiedId === role.id ? (
                              <Check className="h-4 w-4 text-emerald-600" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </td>

                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex items-center gap-1 rounded-2xl border border-slate-100 bg-slate-50 p-1">
                          <button
                            onClick={() => openEditModal(role)}
                            className="inline-flex items-center justify-center rounded-xl p-2 text-slate-600 transition hover:bg-white hover:text-slate-950 hover:shadow-sm"
                            title="Edit role"
                          >
                            <Edit3 size={16} />
                          </button>

                          <button
                            onClick={() => setDeleteTarget(role)}
                            className="inline-flex items-center justify-center rounded-xl p-2 text-rose-600 transition hover:bg-white hover:text-rose-700 hover:shadow-sm"
                            title="Delete role"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid gap-3 p-4 lg:hidden">
              {filteredRoles.map((role) => (
                <div
                  key={role.id}
                  className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <RoleAvatar name={role.name} />

                      <div className="min-w-0">
                        <h3 className="truncate text-base font-black text-slate-950">
                          {role.name}
                        </h3>

                        <p className="text-xs font-semibold text-slate-400">
                          System access role
                        </p>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-1 rounded-2xl border border-slate-100 bg-slate-50 p-1">
                      <button
                        onClick={() => openEditModal(role)}
                        className="rounded-xl p-2 text-slate-600 hover:bg-white"
                        title="Edit role"
                      >
                        <Edit3 size={15} />
                      </button>

                      <button
                        onClick={() => setDeleteTarget(role)}
                        className="rounded-xl p-2 text-rose-600 hover:bg-white"
                        title="Delete role"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <RoleBadge name={role.name} />

                    <button
                      onClick={() => copyRoleId(role.id)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-black text-slate-600"
                    >
                      {copiedId === role.id ? (
                        <Check className="h-3.5 w-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                      {copiedId === role.id ? "Copied" : "Copy ID"}
                    </button>
                  </div>

                  <div className="mt-3 rounded-2xl bg-slate-50 px-3 py-2">
                    <p className="text-[11px] font-black uppercase tracking-wide text-slate-400">
                      Role ID
                    </p>
                    <p className="mt-1 truncate font-mono text-xs font-semibold text-slate-500">
                      {role.id}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <RoleModal
        open={showModal}
        mode={modalMode}
        form={form}
        error={formError}
        submitting={submitting}
        onClose={closeModal}
        onSubmit={handleSubmit}
        onChange={setForm}
      />

      <DeleteDialog
        open={Boolean(deleteTarget)}
        role={deleteTarget}
        loading={deleteLoading}
        onClose={() => {
          if (!deleteLoading) setDeleteTarget(null);
        }}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default RolesPage;
